<script setup lang="ts">
import { Switch, SwitchGroup, SwitchDescription } from '@headlessui/vue';
import Accessible from '../Accessible.vue';
import { computed } from 'vue';
import { useField } from 'vee-validate';
import VPInputBox from './VPInputBox.vue';
import VPIcon from '../VPIcon.vue';
import VPButton from '../VPButton.vue';

type variantKey = 'toggle' | 'checkbox';

type MembershipValue = string | number;

type Props = {
  id?: string;
  name: string;
  label?: string;
  accessibleLabel?: string;
  hint?: string;
  suffix?: string;
  prefix?: string;
  disabled?: boolean;
  variant?: variantKey;
  /**
   * Turns this control into one option of a **multi-select group**: render one instance per
   * option, all sharing a single `name`, and the field holds an **array** of the checked
   * `value`s. Omit it and the component keeps its original behaviour — one control over one
   * boolean field.
   *
   * vee-validate owns the array either way; there is no `v-model` here.
   */
  value?: MembershipValue;
  /**
   * Whether this option's value survives its own unmount. Only meaningful in group mode,
   * where it defaults to `true` — vee-validate's default is to splice an unmounting option's
   * value back out of the array, and for a group rendered from fetched data that is wrong:
   * the options come and go with the read, and a selection quietly emptied by a re-render is
   * indistinguishable from one the operator cleared. Pass `false` for an option whose
   * disappearance really should retract its own value.
   *
   * In boolean mode this is left unset, so vee-validate's own default still applies.
   */
  keepValueOnUnmount?: boolean;
  /**
   * Off for a group whose owner renders the shared field error once beside the list — every
   * option in a group shares one field, so N of them would otherwise print the same message.
   * `aria-invalid` stays on the control either way.
   */
  showError?: boolean;
  /**
   * Let a long label wrap onto further lines instead of truncating. Off by default, because
   * the single-control layout this component started with relies on staying one line high.
   */
  wrapLabel?: boolean;
};

const props = withDefaults(defineProps<Props>(), {
  disabled: false,
  variant: 'toggle',
  // Vue casts an absent Boolean prop to `false`, so this needs a real default rather than a
  // `?? true` at the point of use — otherwise every group would silently opt out.
  keepValueOnUnmount: true,
  showError: true,
  wrapLabel: false,
});

// A group shares one `name`, so the name alone cannot identify an option — the value has to
// ride along or every option in the group would render the same DOM id.
const uid = computed(() => {
  if (props.id) {
    return props.id;
  }

  if (props.value === undefined) {
    return `vp-input-${props.name}`;
  }

  return `vp-input-${props.name}-${String(props.value)}`;
});

/**
 * One control over a boolean, or one option of a group over an array — vee-validate has to be
 * told which at registration time, and `value` cannot change a mounted instance's mode, so
 * the branch is stable for the component's life.
 *
 * The group form is what makes the field value an array at all: vee-validate only takes its
 * array branch for a path registered as a checkbox path. Getters rather than raw props
 * because every option is read through `toValue`, so an option re-keyed to a different value
 * stays bound to the one it is currently rendering.
 */
const registerField = () => {
  if (props.value === undefined) {
    return useField(() => props.name);
  }

  return useField<unknown>(() => props.name, undefined, {
    type: 'checkbox',
    checkedValue: () => props.value,
    keepValueOnUnmount: () => props.keepValueOnUnmount,
  });
};

const field = registerField();
const { errorMessage, meta } = field;

// "On" means the boolean is true, or — in group mode — that this option's value is in the
// array. `checked` is vee-validate's own array-membership computed.
const isOn = computed(() => {
  if (props.value === undefined) {
    return !!field.value.value;
  }

  return !!field.checked?.value;
});

// In group mode the patched handler toggles this option's value in and out of the array, so it
// takes the value rather than the next boolean state.
const onToggle = (next: boolean) => {
  if (props.value === undefined) {
    field.handleChange(next);
    return;
  }

  // Load-bearing. vee-validate only takes its array branch when the field value already *is*
  // an array, or when a second option has registered the same path. Normalising first means a
  // group of one, over a field with no initial value, still writes a list (`[7]`) rather than
  // the bare value (`7`) — and doing it on the click rather than at mount is what keeps an
  // untouched form from reading dirty, since writing a value never moves `initialValues`.
  if (!Array.isArray(field.value.value)) {
    field.value.value = [];
  }

  field.handleChange(props.value);
};

const labelClasses = computed(() => {
  if (props.wrapLabel) {
    return 'whitespace-normal text-left';
  }

  return 'truncate';
});

/**
 * The switch *track* — the pill the knob slides along. It sits on an inner element rather
 * than on the button, because the button also has to hold the label: a labelled toggle used
 * to crush its own text into a 32px capsule, since the button was the pill and the label was
 * inside it with `truncate`.
 */
const trackStyle = 'relative inline-flex h-5 w-8 shrink-0 items-center rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out';

// The cursor and focus ring were on the pill before it moved inside, and the ring's colour was
// picked against it — so they stay with the toggle variant rather than spreading to the
// checkbox one, which has never had a ring and would want a different colour against a pale
// background anyway.
const switchClasses = computed(() => {
  const alignment = props.wrapLabel ? 'items-start' : 'items-center';

  if (props.variant === 'checkbox') {
    return alignment;
  }

  return `${alignment} cursor-pointer focus:outline-hidden focus-visible:ring-2 focus-visible:ring-surface/75`;
});
</script>

<template>
  <VPInputBox
    :class="{ dirty: meta.dirty, 'has-error': errorMessage }"
  >
    <!--
      The group is what puts `aria-describedby` on the switch: headlessui owns that
      attribute and drops any value bound directly on `Switch`, so descriptions have
      to register themselves through `SwitchGroup`.
    -->
    <SwitchGroup as="template">
      <Switch
        :id="`${uid}`"
        :model-value="isOn"
        :as="VPButton"
        type="button"
        v-bind="$attrs"
        :disabled="disabled"
        class="flex gap-1"
        :class="switchClasses"
        role="switch"
        :aria-checked="isOn"
        :aria-disabled="disabled ? 'true' : undefined"
        :aria-invalid="!!errorMessage"
        @update:model-value="onToggle"
      >
        <!-- The track and the label are siblings, so the label lives beside the pill rather
             than inside it. -->
        <span
          v-if="variant === 'toggle'"
          aria-hidden="true"
          :class="[trackStyle, isOn ? 'bg-primary' : 'bg-primary-selection']"
        >
          <span
            :class="isOn ? 'translate-x-3' : 'translate-x-0'"
            class="pointer-events-none inline-block h-4 w-4 transform rounded-full bg-surface shadow-lg ring-0 transition duration-200 ease-in-out"
          />
        </span>
        <span
          v-if="prefix"
          class="min-w-0 truncate text-content"
        >{{ prefix }}</span>
        <VPIcon
          v-if="variant === 'checkbox'"
          :icon="isOn ? 'mdi:checkbox-marked' : 'mdi:checkbox-blank-outline'"
          class="shrink-0 text-xl"
          :class="{ 'text-content': isOn }"
        />

        <label
          v-if="label"
          :id="uid + '-label'"
          :for="uid"
          class="min-w-0 cursor-pointer text-content"
          :class="labelClasses"
        >
          {{ props.label }}
        </label>
      </Switch>

      <!-- `as="div"` rather than the default `p`, so the slot may carry block-level content. -->
      <SwitchDescription
        v-if="hint || $slots.hint"
        :id="uid + '-hint'"
        as="div"
      >
        <slot name="hint">
          <span class="text-text-muted text-xs">{{ hint }}</span>
        </slot>
      </SwitchDescription>

      <SwitchDescription
        v-if="showError && errorMessage"
        :id="uid + '-error'"
        as="span"
        class="text-error font-normal text-xs"
        role="alert"
        aria-live="assertive"
      >
        {{ errorMessage }}
      </SwitchDescription>
    </SwitchGroup>

    <Accessible v-if="accessibleLabel">
      <label :for="uid">{{ accessibleLabel }}</label>
    </Accessible>

    <slot name="suffix">
      <span v-if="suffix">{{ suffix }}</span>
    </slot>
  </VPInputBox>
</template>
